import { useState, useEffect } from 'react'

import { Select, Switch, TextInput, ActionIcon, Divider } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  UPDATE_JOB_LISTING_QUESTION_TEMPLATE,
  REMOVE_JOB_LISTING_QUESTION_TEMPLATE,
} from 'src/graphql/joblistingsQuestionTemplates.graphql'
import IconClose from 'src/icons/IconClose'
import IconRightArrow from 'src/icons/IconRightArrow'
import SettingsField from 'src/pages/SettingsPage/components/SettingsField'

const OPERATOR_OPTIONS = [
  { value: 'EQUALS', label: 'Equals' },
  { value: 'NOT_EQUALS', label: 'Not equals' },
  { value: 'GT', label: 'Greater than' },
  { value: 'LT', label: 'Less than' },
  { value: 'GTE', label: 'Greater than or equal to' },
  { value: 'LTE', label: 'Less than or equal to' },
  { value: 'IS_TRUE', label: 'Is true' },
  { value: 'IS_FALSE', label: 'Is false' },
]

const JobQuestionTemplate = ({
  jobListingId,
  jobQuestionTemplate,
  handleReorder,
  index,
  refetchQuestionTemplates,
  templatesLength,
}) => {
  const [updateQuestionTemplate] = useMutation(
    UPDATE_JOB_LISTING_QUESTION_TEMPLATE,
    {
      onCompleted: () => {
        toast('Question template updated successfully', 'success')
        refetchQuestionTemplates()
      },
      onError: (error) => {
        toast(`Failed to update question template: ${error.message}`, 'error')
      },
    }
  )

  const [removeQuestionTemplate] = useMutation(
    REMOVE_JOB_LISTING_QUESTION_TEMPLATE,
    {
      onCompleted: () => {
        toast('Question template removed successfully', 'success')
        refetchQuestionTemplates()
      },
      onError: (error) => {
        toast(`Failed to remove question template: ${error.message}`, 'error')
      },
    }
  )

  const handleUpdateQuestionTemplate = (
    questionTemplateId: string,
    updates: any
  ) => {
    updateQuestionTemplate({
      variables: {
        jobListingId,
        questionTemplateId,
        input: updates,
      },
    })
  }

  const handleRemoveQuestionTemplate = (questionTemplateId: string) => {
    removeQuestionTemplate({
      variables: {
        jobListingId,
        questionTemplateId,
      },
    })
  }

  const [localComparisonValue, setLocalComparisonValue] = useState(
    jobQuestionTemplate.comparisonValue || ''
  )

  // Keep local state in sync with prop changes
  useEffect(() => {
    if (localComparisonValue !== (jobQuestionTemplate.comparisonValue || '')) {
      setLocalComparisonValue(jobQuestionTemplate.comparisonValue || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuestionTemplate.comparisonValue])

  // Debounced update for comparison value using useEffect
  useEffect(() => {
    if (!jobQuestionTemplate.disqualifier) return
    const handler = setTimeout(() => {
      if (
        localComparisonValue !== (jobQuestionTemplate.comparisonValue || '')
      ) {
        handleUpdateQuestionTemplate(jobQuestionTemplate.questionTemplateId, {
          comparisonValue: localComparisonValue,
          disqualifier: jobQuestionTemplate.disqualifier,
        })
      }
    }, 500)
    return () => clearTimeout(handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localComparisonValue, jobQuestionTemplate.disqualifier])

  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-h-16 w-full flex-row items-center gap-4">
        <div className="flex flex-col items-center justify-center">
          {index > 0 && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => handleReorder(index, index - 1)}
            >
              <IconRightArrow className="h-4 -rotate-90 fill-none stroke-doubleNickel-gray-400" />
            </ActionIcon>
          )}
          {index < templatesLength - 1 && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => handleReorder(index, index + 1)}
            >
              <IconRightArrow className="h-4 rotate-90 fill-none stroke-doubleNickel-gray-400" />
            </ActionIcon>
          )}
        </div>
        <div className="flex w-full flex-col gap-3">
          <SettingsField
            title={jobQuestionTemplate.questionTemplate.question}
            description={`${
              jobQuestionTemplate.questionTemplate.shortQuestionName ||
              jobQuestionTemplate.questionTemplate.variableName
            } · ${jobQuestionTemplate.voiceAgent.name}`}
          >
            <div className="flex flex-row items-center gap-2">
              <Switch
                label="Is disqualifier"
                checked={jobQuestionTemplate.disqualifier}
                labelPosition="left"
                onChange={(e) =>
                  handleUpdateQuestionTemplate(
                    jobQuestionTemplate.questionTemplateId,
                    {
                      disqualifier: e.currentTarget.checked,
                    }
                  )
                }
                size="xs"
              />

              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() =>
                  handleRemoveQuestionTemplate(
                    jobQuestionTemplate.questionTemplateId
                  )
                }
              >
                <IconClose className="h-3 fill-none stroke-doubleNickel-gray-700" />
              </ActionIcon>
            </div>
          </SettingsField>
        </div>
      </div>
      {jobQuestionTemplate.disqualifier && (
        <div className="flex flex-row items-center justify-end gap-2">
          <Select
            placeholder="Operator"
            data={OPERATOR_OPTIONS}
            value={jobQuestionTemplate.operator || ''}
            onChange={(value) =>
              handleUpdateQuestionTemplate(
                jobQuestionTemplate.questionTemplateId,
                {
                  operator: value,
                  disqualifier: jobQuestionTemplate.disqualifier,
                }
              )
            }
            clearable
            size="xs"
          />
          <TextInput
            placeholder="Comparison Value"
            value={localComparisonValue}
            onChange={(e) => {
              setLocalComparisonValue(e.target.value)
            }}
            size="xs"
          />
        </div>
      )}
      <Divider />
    </div>
  )
}

export default JobQuestionTemplate
